import { Formik, Form } from 'formik'
import { useMediaQuery } from 'react-responsive'
import { useMemo, useCallback, useState } from 'react'
import { Alert } from '@windmill/react-ui'

import Head from 'next/head'

import Input from '../components/base/TextBox'
import TextArea from '../components/base/TextArea'
import Button from '../components/base/Button'
import ContactInfo from '../components/ContactInfo'

import contactValidation from '../validations/contact'

import { ClientOnly } from 'react-client-only'

const metaTitle =
    'Contact us for technical consultation | ' +
    process.env.NEXT_PUBLIC_APP_NAME

const metaDescription =
    'Talk with our technical consultant to explore the limitless possibilities and discover techniques of AI & Machine Learning to transform your business.'

const metaImage = ''

const ContactPage = () => {
    const isDesktopOrLaptop = useMediaQuery({
        query: '(min-width: 1024px)',
    })

    const isIpadMobile = useMediaQuery({
        query: '(max-width: 1023px)',
    })

    const [successMessage, setSuccessMessage] = useState(null)

    const initialState = useMemo(
        () => ({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
        }),
        []
    )

    const handleSubmit = useCallback(
        async ({ name, email, phone, company, message }, { resetForm }) => {
            const response = await fetch('/api/contact', {
                body: JSON.stringify({
                    name,
                    email,
                    phone,
                    company,
                    message,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
            })

            if (response.ok) {
                const { message } = await response.json()
                setSuccessMessage(message)

                resetForm()
            }
        },
        []
    )

    const handleChange = useCallback(() => {
        if (successMessage) {
            setSuccessMessage(null)
        }
    }, [successMessage])

    return (
        <>
            <Head>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={metaImage} />
                <meta
                    property="og:url"
                    content={`${process.env.NEXT_PUBLIC_APP_URL}/contact`}
                />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
            </Head>
            <div
                className="container min-h-[calc(100vh-233.01px-102px)] tablet:mt-6"
                itemScope
                itemType="https://schema.org/WebPage"
            >
                <div className="flex pb-12 ipad:flex-col">
                    <div className="pr-12 ipad:pr-0">
                        <div>
                            <h1
                                className="mb-6 text-3xl text-primary-900 tablet:text-2xl"
                                itemProp="headline"
                            >
                                Let&apos;s continue the conversation with our
                                <span className="pl-2 text-primary-500">
                                    Expert
                                </span>
                            </h1>
                            <p
                                className="mb-6 text-base text-interface-900"
                                itemProp="description"
                            >
                                Whether you want to learn more about Smart
                                Answers, or need to speak with a data science
                                consultant, we&apos;re here to help!
                            </p>
                            <div className="mb-6 separator"></div>
                        </div>
                        <ClientOnly>
                            {isDesktopOrLaptop && <ContactInfo />}
                        </ClientOnly>
                    </div>

                    {/* Form */}
                    <Formik
                        onSubmit={handleSubmit}
                        initialValues={initialState}
                        validationSchema={contactValidation}
                    >
                        {({ errors }) => {
                            return (
                                <Form noValidate onChange={handleChange}>
                                    <div className="flex-shrink-0 w-[640px] ipad:w-full ml-auto p-6 bg-interface-100 rounded-xl">
                                        <h5 className="mb-6 font-semibold">
                                            Fill out the form below, and
                                            we&apos;ll get back to you asap.
                                        </h5>
                                        <div>
                                            {!!errors[
                                                Object.keys(errors)[0]
                                            ] && (
                                                <div className="mb-6">
                                                    <Alert type="danger">
                                                        {
                                                            errors[
                                                                Object.keys(
                                                                    errors
                                                                )[0]
                                                            ]
                                                        }
                                                    </Alert>
                                                </div>
                                            )}

                                            {successMessage && (
                                                <div className="mb-6">
                                                    <Alert type="success">
                                                        {successMessage}
                                                    </Alert>
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-6 tablet:grid-cols-1 tablet:gap-0">
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                label="Full Name"
                                                placeholder="Enter your full name"
                                            />
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                label="Email address"
                                                placeholder="Enter your email address"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-6 tablet:grid-cols-1 tablet:gap-0">
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="text"
                                                label="Phone number"
                                                placeholder="Enter your phone number"
                                            />
                                            <Input
                                                id="company"
                                                name="company"
                                                type="text"
                                                label="Company"
                                                placeholder="Enter your company name"
                                            />
                                        </div>
                                        <div>
                                            <div>
                                                <TextArea
                                                    id="message"
                                                    name="message"
                                                    type="text"
                                                    label="Message"
                                                    rows="4"
                                                    placeholder="Start typing your message"
                                                />
                                            </div>

                                            <div className="w-auto">
                                                <Button
                                                    id="contact_submit"
                                                    text="Submit"
                                                    type="submit"
                                                    variant="primary"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>
                    <ClientOnly>
                        {isIpadMobile && (
                            <div className="mt-10">
                                <ContactInfo />
                            </div>
                        )}
                    </ClientOnly>
                </div>
            </div>
        </>
    )
}

export default ContactPage
